import React, { useState } from 'react';
import { aiService } from '../../services/aiService';
import { Loader2, Sparkles, CheckCircle2, AlertTriangle, Activity, ShieldAlert } from 'lucide-react';

interface IncidentAnalysisResult {
  summary: string;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  rootCause: string;
  affectedSystems: string;
  correctiveActions: string;
  mitigationSteps: string[];
}

interface IncidentAdvisorCardProps {
  description: string;
  onAnalysisComplete: (result: IncidentAnalysisResult) => void;
}

export const IncidentAdvisorCard: React.FC<IncidentAdvisorCardProps> = ({ description, onAnalysisComplete }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<IncidentAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyze = async () => {
    if (!description) return;
    setAnalyzing(true);
    setError(null);
    try {
      const analysis = await aiService.analyzeIncident(description);
      setResult(analysis);
    } catch (err: any) {
      setError(err.message || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleApply = () => {
    if (result) {
      onAnalysisComplete(result);
    }
  };

  return (
    <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-4">
      <div className="flex justify-between items-center mb-3">
        <h4 className="text-sm font-semibold text-indigo-900 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-600" />
          AI Incident Advisor
        </h4>
        {!result && (
          <button
            onClick={handleAnalyze}
            disabled={analyzing || !description}
            className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
          >
            {analyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Analyze Incident'}
          </button>
        )}
      </div>

      {error && <div className="text-xs text-red-600 mb-2">{error}</div>}

      {result && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white p-2 rounded border border-indigo-100">
              <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Type</div>
              <div className="text-sm font-medium text-gray-800">{result.type}</div>
            </div>
            <div className="bg-white p-2 rounded border border-indigo-100">
              <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Severity</div>
              <div className={`text-sm font-medium px-2 py-0.5 rounded inline-block ${
                result.severity === 'critical' ? 'bg-red-100 text-red-800' :
                result.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                result.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {result.severity.toUpperCase()}
              </div>
            </div>
          </div>

          <div className="bg-white p-3 rounded border border-indigo-100">
            <div className="text-xs text-gray-500 uppercase font-semibold mb-1 flex items-center gap-1">
              <Activity className="w-3 h-3" /> Root Cause
            </div>
            <div className="text-sm text-gray-700">{result.rootCause}</div>
          </div>

          <div className="bg-white p-3 rounded border border-indigo-100">
            <div className="text-xs text-gray-500 uppercase font-semibold mb-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Affected Systems
            </div>
            <div className="text-sm text-gray-700">{result.affectedSystems}</div>
          </div>

          <div className="bg-white p-3 rounded border border-indigo-100">
            <div className="text-xs text-gray-500 uppercase font-semibold mb-1 flex items-center gap-1">
              <ShieldAlert className="w-3 h-3" /> Corrective Actions
            </div>
            <div className="text-sm text-gray-700">{result.correctiveActions}</div>
          </div>

          <div className="flex justify-end gap-2 mt-2">
            <button 
              onClick={() => setResult(null)}
              className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1"
            >
              Discard
            </button>
            <button 
              onClick={handleApply}
              className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded hover:bg-indigo-700 flex items-center gap-1"
            >
              <CheckCircle2 className="w-3 h-3" /> Apply to Form
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
